import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { tileMetric } from './TileAlertModal.jsx';

// 🎯 "Set a goal" from a dashboard tile — the in-context sheet, sibling of the
// 🔔 TileAlertModal and deliberately the same shape: the user is ALREADY looking
// at the number, so everything about *what* to track is known. The sheet asks the
// one question that's left ("what are you aiming for, by when?") and gets out of
// the way. The saved goal is a normal tile-sourced goal — it lists, edits and
// tracks live on the Goals page exactly like one built there.
//
// Pre-filled from the tile: the metric ref (server-derived — never trusted from
// here), the tile's title (→ the goal name), its live on-screen value (the
// starting point the target is judged against) and a unit guessed from how the
// tile renders. The event's date is the natural "by when" default.
//
// Mobile-first: full-screen portaled sheet on a phone, one column, 44px controls,
// sticky action bar above the safe area; a centred card on desktop.
//
// Dual-surface by construction: the same component serves a client self-serving
// and a Howler admin on the client's dashboard — the server decides who may
// write (goals.manage) and reports it back as `canManage`.
export default function TileGoalModal({ tile, data, suiteId, suiteName = '', dashboardId, onClose }) {
  const isMobile = useIsMobile();
  const guess = useMemo(() => tileMetric(tile, data), [tile, data]);

  const [ctx, setCtx] = useState(null);          // server context (permission, event date, existing)
  const [loadErr, setLoadErr] = useState('');
  const [target, setTarget] = useState('');
  const [unit, setUnit] = useState(guess.unit);
  const [direction, setDirection] = useState('at_least'); // at_least | at_most
  const [byDate, setByDate] = useState('');
  const [name, setName] = useState(tile?.title ? `${String(tile.title).slice(0, 100)} target` : '');
  const [more, setMore] = useState(false);        // the fine-tune disclosure
  const [display, setDisplay] = useState('bar');
  const [tag, setTag] = useState('');
  const [northStar, setNorthStar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(null);       // the created goal → success state

  // Lock the page behind the sheet + close on Escape (mirrors TileAlertModal).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Who may create one here, the event's date, and what already tracks this tile.
  const tileId = tile?.id || '';
  useEffect(() => {
    let alive = true;
    if (!suiteId || !dashboardId || !tileId) return undefined;
    api.tileGoalContext(suiteId, dashboardId, tileId)
      .then((r) => { if (alive) { setCtx(r); if (r.eventDate) setByDate((d) => d || String(r.eventDate).slice(0, 10)); } })
      .catch((e) => { if (alive) setLoadErr(e.message || 'Could not open the goal setup here.'); });
    return () => { alive = false; };
  }, [suiteId, dashboardId, tileId]);

  const canManage = ctx ? !!ctx.canManage : false;
  const eventName = ctx?.suiteName || suiteName;
  const metricName = ctx?.tileName || tile?.title || 'this number';

  // The plain-English read-back, so the goal is never a mystery before saving.
  const sentence = () => {
    const t = target === '' ? '…' : fmtNum(Number(target), unit);
    const aim = direction === 'at_most' ? `Keep ${metricName} at or under ${t}` : `Get ${metricName} to ${t}`;
    const by = byDate ? ` by ${byDate}` : '';
    return `${aim}${by} — tracked live from this tile${northStar ? ', as the event’s North Star' : ''}.`;
  };

  async function save() {
    if (target === '' || Number.isNaN(Number(target))) { setErr('Set the number you’re aiming for.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.createTileGoal(suiteId, {
        dashboardId, tileId,
        name: (name || '').trim() || `${metricName} target`,
        targetValue: Number(target),
        unit, direction, byDate, display,
        tag: tag.trim(), isNorthStar: northStar,
      });
      if (r?.error) throw new Error(r.error);
      setSaved(r.goal);
    } catch (e) { setErr(e.message || 'Could not save the goal.'); }
    finally { setBusy(false); }
  }

  const body = (
    <div style={sheet(isMobile)} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Set a goal from this tile">
      <div style={head(isMobile)}>
        <span style={{ fontSize: 19 }}>🎯</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>{saved ? 'Goal set' : 'Set a goal'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {metricName}{eventName ? ` · ${eventName}` : ''}
          </div>
        </div>
        <button onClick={onClose} style={xBtn} aria-label="Close">✕</button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '14px 16px' : '14px 18px' }}>
        {/* ── Saved: confirm + the one link out (editable in Goals, as promised) ── */}
        {saved ? (
          <div>
            <div style={okBox}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>✅ {metricName} now has a target.</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>{sentence()}</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, lineHeight: 1.5 }}>
              Progress reads live off this tile and shows on the Goals page (and in briefings
              once it matters). Fine-tune it — checkpoints, baselines, pace — any time in Goals.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <Link to="/goals" style={{ ...btnGhost, textDecoration: 'none', textAlign: 'center' }} onClick={onClose}>Manage in Goals →</Link>
              <button onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          </div>
        ) : loadErr ? (
          <div style={{ fontSize: 13, color: 'var(--error, #dc2626)', lineHeight: 1.5 }}>{loadErr}</div>
        ) : !ctx ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[70, 100, 60].map((w, i) => <div key={i} className="skel" style={{ width: `${w}%`, height: 34, borderRadius: 10 }} />)}
          </div>
        ) : !canManage ? (
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>
            You don’t have permission to set goals for this event. Ask an owner on your team
            (or your Howler account manager) — they can do it from this same tile.
          </div>
        ) : (<>
          {/* Where it stands right now — straight off the tile on screen. */}
          <div style={readsBox}>
            <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Now:</span>
            <b style={{ fontSize: 15 }}>{guess.display || '—'}</b>
            {guess.value != null && (
              <>
                <span style={{ flex: 1 }} />
                <button type="button" onClick={() => setTarget(String(Math.round(guess.value)))} style={miniBtn}>Start from this number</button>
              </>
            )}
          </div>

          {/* Goals already tracking this tile — no silent duplicates. */}
          {!!(ctx.existing || []).length && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0', lineHeight: 1.45 }}>
              Already tracking this tile: {ctx.existing.map((g) => g.name).join(', ')}.{' '}
              <Link to="/goals" onClick={onClose} style={{ color: 'var(--brand)', fontWeight: 700 }}>Manage</Link>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <Field label="Aim" style={{ flex: '1 1 150px' }}>
              <select value={direction} onChange={(e) => setDirection(e.target.value)} style={inp}>
                <option value="at_least">Reach at least ↑</option>
                <option value="at_most">Stay at or under ↓</option>
              </select>
            </Field>
            <Field label="Target" style={{ flex: '1 1 120px' }}>
              <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 10000" inputMode="decimal" style={inp} autoFocus={!isMobile} />
            </Field>
            <Field label="Unit" style={{ flex: '0 0 108px' }}>
              <select value={unit} onChange={(e) => setUnit(e.target.value)} style={inp}>
                {[...new Set(['tickets', 'ZAR', '%', 'orders', 'count', unit])].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>

          <Field label="By when" hint="Defaults to the event date — the deadline progress is paced against.">
            <input type="date" value={byDate} onChange={(e) => setByDate(e.target.value)} style={inp} />
          </Field>

          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${metricName} target`} style={inp} />
          </Field>

          {/* Everything else has a sane default — hidden until asked for, so the
              common path stays "one number, Set goal". */}
          <button type="button" onClick={() => setMore((v) => !v)} style={discloseBtn}>{more ? '▾' : '▸'} Fine-tune (look, category, North Star)</button>
          {more && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Field label="Shown as" style={{ flex: '1 1 150px' }}>
                  <select value={display} onChange={(e) => setDisplay(e.target.value)} style={inp}>
                    <option value="bar">Progress bar</option>
                    <option value="dial">Dial</option>
                    <option value="ring">Ring</option>
                  </select>
                </Field>
                <Field label="Category (optional)" style={{ flex: '1 1 150px' }}>
                  <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. Ticketing" style={inp} />
                </Field>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, fontWeight: 600, minHeight: 40, cursor: 'pointer' }}>
                <input type="checkbox" checked={northStar} onChange={(e) => setNorthStar(e.target.checked)} style={{ width: 17, height: 17 }} />
                Make this the event’s ⭐ North Star{ctx.hasGoals ? '' : ' (the first goal becomes it anyway)'}
              </label>
            </div>
          )}

          <div style={sentenceBox}>{sentence()}</div>
          {/* Be straight about WHAT gets tracked: the tile as this event has it set
              up — not a filter being tried out on the dashboard right now. */}
          <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0', lineHeight: 1.45 }}>
            📍 Tracks this tile’s live number{eventName ? <> for <b style={{ color: 'var(--text)' }}>{eventName}</b></> : null}, as the
            event has it set up — and only your own data. A filter you change on the dashboard right
            now isn’t part of the goal; checkpoints, baselines and pace curves live on the{' '}
            <Link to="/goals" onClick={onClose} style={{ color: 'var(--brand)', fontWeight: 700 }}>Goals</Link> page.
          </div>
          {err && <div style={{ color: 'var(--error, #dc2626)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
        </>)}
      </div>

      {/* Sticky action bar — thumb-reachable on a phone, clear of the home bar. */}
      {!saved && canManage && !loadErr && (
        <div style={actions(isMobile)}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Saving…' : 'Set goal'}</button>
        </div>
      )}
    </div>
  );

  return createPortal(
    <div style={overlay(isMobile)} onClick={onClose}>{body}</div>,
    document.body
  );
}

function fmtNum(v, unit) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString('en-ZA') : String(n);
  if (unit === 'ZAR') return `R${s}`;
  if (unit === '%') return `${s}%`;
  return unit && unit !== 'count' ? `${s} ${unit}` : s;
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}

// Full-screen on a phone; a centred card on desktop (same skin as TileAlertModal).
const overlay = (isMobile) => ({
  position: 'fixed', inset: 0, zIndex: 420, background: isMobile ? 'var(--bg, #fff)' : 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16,
});
const sheet = (isMobile) => ({
  display: 'flex', flexDirection: 'column', width: '100%', color: 'var(--text)', background: 'var(--card)',
  ...(isMobile
    ? { height: '100%', borderRadius: 0 }
    : { maxWidth: 460, maxHeight: '90vh', borderRadius: 16, border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg, 0 12px 40px rgba(0,0,0,0.28))' }),
});
const head = (isMobile) => ({
  display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
  padding: isMobile ? 'calc(12px + env(safe-area-inset-top)) 16px 12px' : '16px 18px 12px',
  borderBottom: '1px solid var(--border)',
});
const actions = (isMobile) => ({
  display: 'flex', gap: 10, flexShrink: 0, borderTop: '1px solid var(--border)', background: 'var(--card)',
  padding: isMobile ? '12px 16px calc(12px + env(safe-area-inset-bottom))' : '12px 18px 14px',
});
const inp = { width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 11px', border: '1.5px solid var(--hairline)', borderRadius: 9, fontSize: 14, outline: 'none', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit' };
const xBtn = { border: 'none', background: 'rgba(128,128,128,0.12)', color: 'var(--muted-2)', borderRadius: 980, width: 32, height: 32, fontSize: 13, cursor: 'pointer', flexShrink: 0 };
const readsBox = { padding: '9px 11px', background: 'rgba(128,128,128,0.07)', borderRadius: 9, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 };
const okBox = { padding: '12px 13px', background: 'rgba(16,185,129,0.10)', border: '1px solid var(--hairline)', borderRadius: 10 };
const miniBtn = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--brand)', borderRadius: 980, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', cursor: 'pointer', flexShrink: 0 };
const discloseBtn = { border: 'none', background: 'transparent', color: 'var(--brand)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: '6px 0', fontFamily: 'inherit' };
const sentenceBox = { marginTop: 12, padding: '11px 13px', background: 'rgba(var(--brand-rgb,10,132,255),0.07)', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 13, lineHeight: 1.5, fontWeight: 600 };
const btnGhost = { flex: '0 0 auto', minHeight: 44, padding: '11px 15px', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnPrimary = { flex: 1, minHeight: 44, padding: '11px 16px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' };
